import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequisition, submitQuotation } from '../api/vendorApi';
import { VpSpinner, VpConfirmModal, money } from '../components/VpUI';

interface Line {
	productId: string;
	name: string;
	unit: string;
	quantity: number;
	product_description: string;
	rate: number | '';
	tax: number | '';
	discount: number | '';
	discountType: 'percentage' | 'fixed';
}

const lineTotal = (l: Line) => {
	const qty = Number(l.quantity) || 0;
	const rate = Number(l.rate) || 0;
	const disc = Number(l.discount) || 0;
	const tax = Number(l.tax) || 0;
	const afterDisc =
		l.discountType === 'fixed' ? qty * (rate - disc) : qty * (rate - (rate * disc) / 100);
	return afterDisc + (afterDisc * tax) / 100;
};

const VendorQuoteForm = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [req, setReq] = useState<any>(null);
	const [lines, setLines] = useState<Line[]>([]);
	const [note, setNote] = useState('');
	const [remarks, setRemarks] = useState('');
	const [vendorQuoteNo, setVendorQuoteNo] = useState('');
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		(async () => {
			try {
				const data = await getRequisition(id as string);
				setReq(data);
				if (data.alreadyQuoted) {
					// Already quoted → send them to the read-only quote.
					navigate(`/vendor-portal/quotations/${data.myQuotation?._id}`, {
						replace: true,
					});
					return;
				}
				const products = (data.products || []).map((p: any) => ({
					productId: p.productId,
					name: p.productInfo?.name || p.product_description || 'Item',
					unit: p.productInfo?.unit?.name || p.productInfo?.unitName || '',
					quantity: p.quantity || 0,
					product_description: p.product_description || '',
					rate: '',
					tax: '',
					discount: '',
					discountType: 'percentage',
				}));
				setLines(products);
			} catch (err: any) {
				setError(err?.apiMessage || 'Could not load the requisition.');
			} finally {
				setLoading(false);
			}
		})();
	}, [id, navigate]);

	const grandTotal = useMemo(() => lines.reduce((s, l) => s + lineTotal(l), 0), [lines]);

	const update = (idx: number, field: keyof Line, value: any) => {
		setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
	};

	const validate = () => {
		if (!lines.length) return 'This requisition has no products.';
		for (const l of lines) {
			if (l.rate === '' || Number(l.rate) <= 0) return `Enter a valid rate for "${l.name}".`;
		}
		return '';
	};

	const openConfirm = () => {
		const v = validate();
		if (v) {
			toast.warn(v);
			return;
		}
		setConfirmOpen(true);
	};

	const doSubmit = async () => {
		setSubmitting(true);
		try {
			const payload = {
				requisitionId: id,
				note,
				remarks,
				vendor_quotation_no: vendorQuoteNo || undefined,
				products: lines.map((l) => ({
					productId: l.productId,
					product_description: l.product_description,
					quantity: Number(l.quantity),
					rate: Number(l.rate),
					tax: Number(l.tax) || 0,
					discount: Number(l.discount) || 0,
					discountType: l.discountType,
					amount_with_tax: Number(lineTotal(l).toFixed(2)),
				})),
			};
			const created = await submitQuotation(payload);
			toast.success('Quotation submitted successfully!');
			navigate(`/vendor-portal/quotations/${created._id}`, { replace: true });
		} catch (err: any) {
			setConfirmOpen(false);
			toast.error(err?.apiMessage || 'Could not submit the quotation.');
		} finally {
			setSubmitting(false);
		}
	};

	if (loading) return <VpSpinner />;
	if (error) return <div className='vp-alert vp-alert-error'>{error}</div>;

	return (
		<div>
			<div className='vp-card vp-card-pad' style={{ marginBottom: 18 }}>
				<div className='vp-row-between'>
					<div>
						<div className='vp-muted' style={{ fontSize: 13 }}>
							Requisition
						</div>
						<div style={{ fontSize: 20, fontWeight: 700 }}>{req?.requisition_no}</div>
					</div>
					<button
						type='button'
						className='vp-btn vp-btn-ghost'
						onClick={() => navigate('/vendor-portal/requisitions')}>
						← Back
					</button>
				</div>
			</div>

			<div className='vp-card'>
				<div className='vp-card-head'>
					<h3>Enter Your Prices</h3>
				</div>
				<div className='vp-table-wrap'>
					<table className='vp-table'>
						<thead>
							<tr>
								<th>Product</th>
								<th>Qty</th>
								<th>Rate</th>
								<th>Discount</th>
								<th>Tax %</th>
								<th style={{ textAlign: 'right' }}>Line Total</th>
							</tr>
						</thead>
						<tbody>
							{lines.map((l, idx) => (
								<tr key={l.productId + idx}>
									<td>
										<div style={{ fontWeight: 600 }}>{l.name}</div>
										{l.product_description && (
											<div className='vp-muted' style={{ fontSize: 12 }}>
												{l.product_description}
											</div>
										)}
									</td>
									<td>
										{l.quantity} {l.unit}
									</td>
									<td>
										<input
											className='vp-input vp-input-sm'
											style={{ width: 100 }}
											type='number'
											min={0}
											value={l.rate}
											onChange={(e) => update(idx, 'rate', e.target.value)}
											placeholder='0.00'
										/>
									</td>
									<td>
										<div style={{ display: 'flex', gap: 6 }}>
											<input
												className='vp-input vp-input-sm'
												style={{ width: 72 }}
												type='number'
												min={0}
												value={l.discount}
												onChange={(e) =>
													update(idx, 'discount', e.target.value)
												}
												placeholder='0'
											/>
											<select
												className='vp-input vp-input-sm'
												style={{ width: 64 }}
												value={l.discountType}
												onChange={(e) =>
													update(idx, 'discountType', e.target.value)
												}>
												<option value='percentage'>%</option>
												<option value='fixed'>₹</option>
											</select>
										</div>
									</td>
									<td>
										<input
											className='vp-input vp-input-sm'
											style={{ width: 72 }}
											type='number'
											min={0}
											value={l.tax}
											onChange={(e) => update(idx, 'tax', e.target.value)}
											placeholder='0'
										/>
									</td>
									<td style={{ textAlign: 'right', fontWeight: 600 }}>
										{money(lineTotal(l))}
									</td>
								</tr>
							))}
						</tbody>
						<tfoot>
							<tr>
								<td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>
									Grand Total
								</td>
								<td style={{ textAlign: 'right', fontWeight: 800, fontSize: 16 }}>
									{money(grandTotal)}
								</td>
							</tr>
						</tfoot>
					</table>
				</div>
			</div>

			<div className='vp-card vp-card-pad vp-mt-16'>
				<div className='vp-grid-2'>
					<div>
						<div className='vp-field'>
							<label className='vp-label'>Note (optional)</label>
							<input
								className='vp-input'
								value={note}
								onChange={(e) => setNote(e.target.value)}
								placeholder='Any note for the buyer'
							/>
						</div>
						<div className='vp-field vp-mb-0'>
							<label className='vp-label'>Remarks (optional)</label>
							<input
								className='vp-input'
								value={remarks}
								onChange={(e) => setRemarks(e.target.value)}
								placeholder='Delivery terms, validity, etc.'
							/>
						</div>
					</div>
					<div>
						<div className='vp-field vp-mb-0'>
							<label className='vp-label'>Your Quotation Ref No. (optional)</label>
							<input
								className='vp-input'
								value={vendorQuoteNo}
								onChange={(e) => setVendorQuoteNo(e.target.value)}
								placeholder='e.g. VQ-2024-001'
							/>
						</div>
					</div>
				</div>
				<div className='vp-row-between vp-mt-16'>
					<div className='vp-muted' style={{ fontSize: 13 }}>
						⚠️ Once submitted, your quotation cannot be changed.
					</div>
					<button type='button' className='vp-btn vp-btn-primary' onClick={openConfirm}>
						Submit Quotation
					</button>
				</div>
			</div>

			<VpConfirmModal
				open={confirmOpen}
				emoji='📝'
				title='Submit this quotation?'
				message='After you submit, your prices are locked and cannot be changed. Please review before confirming.'
				confirmText='Yes, submit'
				cancelText='Review again'
				loading={submitting}
				onConfirm={doSubmit}
				onCancel={() => setConfirmOpen(false)}
			/>
		</div>
	);
};

export default VendorQuoteForm;
